import { AuthService } from '@/auth/service/auth.service';
import { DataToSend, EquipmentToSend, EquipmentToShow, LicenseType, VehicleType, Zone } from '@/interfaces/new-license-request/new-license-request-interfaces';
import { Person } from '@/interfaces/person';
import { CreateRequirement, RequestDocuments, RequiredDocument } from '@/interfaces/requirement';
import { AssetsFilesService } from '@/services/assetsFilesService/assets-files.service';
import { CommonService } from '@/services/common/common.service';
import { PersonService } from '@/services/person/person.service';
import { RequirementService } from '@/services/requirement/requirement.service';
import { BoxColors } from '@/shared/person-card/person-card.component';
import { Option, SelectListComponent } from '@/shared/select-list/select-list.component';
import { StoreState } from '@/store';
import { State } from '@/store/login/reducer';
import { getCurrentUserData } from '@/store/login/selectors';
import { modalShow } from '@/store/modal/actions';
import { loaderAnimationHide, loaderAnimationShow } from '@/store/page-loader/action';
import { normalize } from '@/utils/normalizeString';
import { ofuscate } from '@/utils/obfuscation';
import { alLeastOneDateEndMustBeNotUndefined } from '@/validators/alLeastOneDateEndMustBeNotUndefined';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { debounceTime, switchMap, tap } from 'rxjs/operators';
import { NewEquipmentRow } from '../../extend/form/new-record/new-record.interfaces';
import { FormGroupWithErrors } from './form-asset-contract/FormGroupWithErrors';

@Component({
  templateUrl: './request.component.html',
  styleUrls: ['./request.component.css'],
})
export class RequestComponent implements OnInit {

  formEmployeeControl = new FormGroup({
    managerFormControl: new FormControl('', [Validators.required]),
    superintendentFormControl: new FormGroup({
      filterText: new FormControl(''),
      id: new FormControl(0),
      mdmid: new FormControl(''),
    }),
  });

  get managerFormControl(): FormControl {
    return this.formEmployeeControl.controls.managerFormControl as FormControl;
  }

  get superintendentFormControl() {
    return this.formEmployeeControl.controls.superintendentFormControl as FormGroup;
  }

  licenseInformationForm: FormGroupWithErrors = new FormGroupWithErrors({
    assetId: new FormControl(0),
    mdmid: new FormControl(''),
    contractId: new FormControl(0),
    isEmployee: new FormControl(false),
    haveLicense: new FormControl(false),
    haveInfractions: new FormControl(false),
    haveRequirement: new FormControl(false),
  });

  get selectedPersonIsEmployee(): boolean {
    return this.licenseInformationForm.controls.isEmployee.value as boolean;
  }

  personForm: FormGroup = new FormGroup({
    filterText: new FormControl(''),
    id: new FormControl(-1),
  });

  contractOwnerForm = new FormControl('', [Validators.required]);

  gettingPerson: boolean = false;
  personBoxColor: BoxColors = BoxColors.BROWN_GREY;

  requirementId: number | undefined;
  requestType: 'personal' | 'third';

  vehicleTypesData: VehicleType[] = [];
  bannedVehicleTypeIds: number[] = [];

  licenseTypesData: LicenseType[] = [];
  zonesData: Zone[] = [];

  equipmentToSend: EquipmentToSend[] = [];
  equipmentToShow: Array<any> = [];

  selectedPersonData?: Person;
  listPersonOptions: Option[] = [];

  loadingPersonOptions: boolean = false;

  requiredDocuments: Partial<RequiredDocument>[] = [];
  storeUserData?: State;

  dataToSend: Partial<DataToSend> = {};
  agreeTerms = new FormControl(false);

  allDocumentsEndDateAreUndefined: boolean = false;

  get formOk(): boolean {
    if (this.documentFormArray.controls.length === 0) {
      return false;
    }
    return this.documentFormArray.valid;
  }

  titleNum: number = 0;

  @ViewChild('SuperintendentComponent')
  childComponent!: SelectListComponent;

  documentFormArray: FormArray = new FormArray([], alLeastOneDateEndMustBeNotUndefined());

  requestDocuments: RequestDocuments[] = [];

  onResetName(): void {
    try {
      this.childComponent.textInputValue.reset();
    } catch (error) {
    }
  }

  constructor(
    private requirementService: RequirementService,
    private personService: PersonService,
    private router: Router,
    private store: Store<StoreState>,
    private commonService: CommonService,
    private auth: AuthService,
    private assetsFilesService: AssetsFilesService,
  ) {
    this.requestType = <'personal' | 'third'>(
      this.router.url.split('/').reverse()[0]
    );
    this.titleNum = this.requestType === 'personal' ? 0 : -1;
    getCurrentUserData(this.store).subscribe({
      next: (userData) => {
        this.storeUserData = userData;
        if (this.requestType === 'personal') {
          this.selectedPersonData = userData;
          this.personBoxColor = BoxColors.WHITE;
          const selectedPersonIsEmployee = normalize(this.selectedPersonData?.entity) === normalize('Employee');
          this.licenseInformationForm.patchValue({ mdmid: userData.mdmid, isEmployee: selectedPersonIsEmployee });
        }
      },
    });

    const { filterText: filterText$ } = this.personForm.controls;

    filterText$.valueChanges
      .pipe(
        tap(() => {
          this.loadingPersonOptions = true;
        }),
        debounceTime(2000),
        switchMap((value) => this.getFilteredList(value)),
      )
      .subscribe({
        next: (options) => {
          this.listPersonOptions = options;
          this.loadingPersonOptions = false;
        },
      });

    this.requirementService.requestNewId().subscribe({
      next: (val) => {
        this.requirementId = val;
      },
    });

  }

  ngOnInit(): void {
    this.onChangeSelectedAsset();
  }

  getFilteredList(value: string) {
    const thirdEmployee = this.auth.userHavePrivilege('LICENSE REQUEST THIRD_EMPLOYEE');
    const thirdContractor = this.auth.userHavePrivilege('LICENSE REQUEST THIRD_CONTRACTOR');

    if (thirdEmployee && thirdContractor) {
      return this.personService.getFilteredList(value);
    }
    if (thirdEmployee) {
      return this.personService.getFilteredListExeptContractor(value);
    }
    if (thirdContractor) {
      return this.personService.getFilteredListExeptEmployee(value);
    }
    // If user have no privileges, does nothing
    return of([]);
  }

  onChangeSelectedAsset() {
    this.licenseInformationForm
      .get('assetId')
      ?.valueChanges.subscribe((assetId: any) => {
        this.getVehicleTypesByAsset(assetId);
        this.getlicenseTypesByAsset(assetId).subscribe({
          next: (licenseTypes: LicenseType[]) => {
            this.licenseTypesData = licenseTypes;
          },
        });
        this.getZonesByAsset(assetId).subscribe({
          next: (zones) => {
            this.zonesData = zones;
          },
        });

        this.equipmentToSend = [];
        this.equipmentToShow = [];
        this.onResetName();
        this.superintendentFormControl.reset();
        this.managerFormControl.patchValue('');
        this.agreeTerms.reset();
        this.getRequiredDocuments();
      });
  }

  onSaveEquipment(equipmentRow: NewEquipmentRow) {
    const valuetoSend: EquipmentToSend = {
      equipmentId: +equipmentRow.vehicleType,
      equipmentBrand: equipmentRow.brand,
      equipmentModel: equipmentRow.model,
      licenseTypes: equipmentRow.licenseTypes.filter((a) => a.active).map((licenseType) => licenseType.id),
      conductionZones: equipmentRow.conductionZones.filter((a) => a.active).map((conductionZone) => conductionZone.id),
    };

    const valueToShow: EquipmentToShow = {
      name: this.vehicleTypesData.find((vehicle) => vehicle.id === +equipmentRow.vehicleType)!.name,
      equipmentBrand: equipmentRow.brand,
      equipmentModel: equipmentRow.model,
      licenseTypes: equipmentRow.licenseTypes.filter((a) => a.active).map((licenseType) => licenseType.name),
      conductionZones: equipmentRow.conductionZones.filter((a) => a.active).map((zone) => zone.name),
    };

    this.dataToSend.equipments = [...(this.dataToSend.equipments || []), valuetoSend];
    this.equipmentToSend.push(valuetoSend);
    this.equipmentToShow.push(valueToShow);

    this.getRequiredDocuments();
  }

  getRequiredDocuments() {
    this.requestDocuments = this.equipmentToSend.map(
      (equipment) => ({
        assetId: this.licenseInformationForm.controls.assetId.value,
        equipmentId: equipment.equipmentId,
        licenseType: equipment.licenseTypes.join(','),
        conductionZone: equipment.conductionZones.join(','),
        vehicleTypeId: 0,
      }),
    );
  }

  getVehicleTypesByAsset(assetId: number) {
    this.commonService.getVehiclesTypesByAsset(assetId).subscribe((res) => {
      this.vehicleTypesData = res;
    });
  }

  getZonesByAsset(assetId: number) {
    const observable$ = this.commonService.getZonesByAsset(assetId);
    this.zonesData = [];
    return observable$;
  }

  getlicenseTypesByAsset(assetId: number) {
    const observable$ = this.commonService.getLicenseTypesByAsset(assetId);
    return observable$;
  }

  onSearchPersonTextChange(text: string) {
    this.personForm.patchValue({ filterText: text });
  }

  onSearchPersonSelected(id: string) {
    this.personForm.patchValue({ id: +id });
  }

  onSelectPerson() {
    const personId = this.personForm.controls.id.value;
    this.gettingPerson = true;
    this.personService.getPersonDataById(personId).subscribe({
      next: (personData) => {
        this.gettingPerson = false;
        this.selectedPersonData = personData;
        this.dataToSend.userMdmid = personData.mdmid;
        const selectedPersonIsEmployee = normalize(this.selectedPersonData?.entity) === normalize('Employee');
        this.licenseInformationForm.patchValue({ mdmid: personData.mdmid, isEmployee: selectedPersonIsEmployee });
      },
    });
  }

  onUnselectDriver() {
    this.personForm.patchValue({ id: -1, filterText: '' });
    this.licenseInformationForm.patchValue({ mdmid: '' });
    this.selectedPersonData = undefined;
    this.listPersonOptions = [];
    this.licenseInformationForm.patchValue({
      assetId: '',
      contractId: 0,
    });
  }

  onRemoveEquipment(indexToRemove: number) {
    this.equipmentToShow.splice(indexToRemove, 1);
    this.equipmentToSend.splice(indexToRemove, 1);
    this.bannedVehicleTypeIds = this.equipmentToSend.map((equipment) => equipment.equipmentId);
    this.getRequiredDocuments();
  }

  showConfirmationModal() {
    this.store.dispatch(
      modalShow({
        title: '¿Estas seguro de enviar esta solicitud?',
        text: `
        Si ya has revisado la información registrada y está correcta, continúa en Enviar solicitud. De lo contrario, ingresa Volver para revisar la información.
      `,
        acceptButtonText: 'Enviar solicitud',
        actionOnAccept: () => {
          this.store.dispatch(loaderAnimationShow());

          const equipments = this.equipmentToSend.map((equipment) => ({
            vehicleType: equipment.equipmentId,
            brand: equipment.equipmentBrand,
            model: equipment.equipmentModel,
            licenseTypes: equipment.licenseTypes,
            drivingAreas: equipment.conductionZones,
            expiredAt: new Date(),
          }));

          const documents = this.documentFormArray.value.map((documentForm: any) => ({
            documentRequiredTypeId: documentForm.id,
            requirementId: <number>this.requirementId,
            fileName: documentForm.file.fileName,
            path: documentForm.file.fileUrl,
            createFrom: <Date>documentForm.dateFrom,
            createTo: <Date>documentForm.dateUntil,
            createAt: <Date>documentForm.dateEmit,
          }));

          const requirement: CreateRequirement = {
            requestorMdmid: this.storeUserData?.mdmid as string,
            driverMdmid: <string>this.selectedPersonData?.mdmid,
            assetId: this.licenseInformationForm.controls.assetId.value,
            contractId: this.licenseInformationForm.controls.contractId.value || '',
            equipments,
            documents,
          };

          if (this.selectedPersonIsEmployee) {
            requirement.superintendenteRutDv = this.superintendentFormControl.controls.mdmid.value;
            requirement.requestManager = this.managerFormControl.value;
          }
          else {
            requirement.contractOwnerMdmid = this.contractOwnerForm.value;
          }

          this.requirementService.createRequirement(requirement, this.requirementId as number).subscribe({
            next: () => {
              const ofuscatedData = ofuscate({
                email: this.selectedPersonData?.email,
                userId: this.selectedPersonData?.id,
                id: this.requirementId,
              });
              this.store.dispatch(loaderAnimationHide());
              this.router.navigateByUrl(`license/new/success/${ofuscatedData}`);
            },
            error: ({ error }) => {
              let ofuscatedData = ofuscate({
                variant: 'failure',
              });
              if (Object.keys(error).includes('httpMessage')) {
                if (error.httpMessage == 'The contract has no CO registered on the DB.') {
                  ofuscatedData = ofuscate({
                    variant: 'failure',
                    errorMessage: 'Hubo un problema y no hemos podido enviar tu solicitud. El contrato seleccionado no tiene un Contract Owner asociado. Por favor, contacta con tu supervisor.'
                  });
                }
              }
              this.store.select('pageError').subscribe({
                next: (pageConfig) => {
                  const [website, ...url] = window.location.href.split('//')[1].split('/');
                  if (pageConfig.errorType !== 'Mulesoft') {
                    this.store.dispatch(loaderAnimationHide());
                    this.router.navigateByUrl(`license/new/failure/${ofuscatedData}`);
                  }
                },
              });
            },
          });
        },
      }),
    );
  }

  showCancelModal() {
    this.store.dispatch(
      modalShow({
        title: '¿Estas seguro de cancelar esta solicitud?',
        text: `
      La solicitud será cancelada automáticamente si continúa en cancelar solicitud. Presiona volver para regresar a la pantalla anterior.
      `,

        acceptButtonText: 'Cancelar solicitud',
        actionOnAccept: () => {
          this.router.navigateByUrl('../');
        },
      }),
    );
  }

  showModalConfirmTermsAndConditions() {
    this.assetsFilesService.getTermsAndConditions().subscribe({
      next: (text) => {
        const parsedText = text.replace(/(?:\r\n|\r|\n)/g, '\\n');

        this.store.dispatch(
          modalShow({
            title: 'Términos y condiciones',
            text: parsedText,
            acceptButtonText: 'Aceptar',
            actionOnAccept: () => { },
          }),
        );
      },
    });
  }

  onSetContractOwner(mdmid: string) {
    this.contractOwnerForm.setValue(mdmid);
  }

  get shouldActivateSubmit() {
    let should = true;
    if (!this.agreeTerms.value || !this.formOk) {
      should = false;
    }

    if (this.selectedPersonIsEmployee) {
      if (
        !(
          this.superintendentFormControl.valid
          && this.managerFormControl.valid
        )
      ) {
        should = false;
      }
    }
    else if (this.contractOwnerForm.invalid) {
      should = false;
    }

    return should;
  }

}
